import { dataSimples } from '../../../lib/datas'
import { reais } from '../../../lib/dinheiro'
import type { DetalheDaOs, ItemDaOs } from '../../../types/ordemDeServico'
import { DOCUMENTO_DA_OS } from './textoDaOs'

/** Uma linha da seção 4, já formatada como ela sai no papel. */
export interface LinhaImpressa {
  item: string
  descricao: string
  metrica: string
  valorUnitario: string
  quantidade: string
  valorTotal: string
  /** A conferência do sistema, quando ela não coincide com o papel. Vazio quando coincidem. */
  conferencia: string
}

/**
 * As linhas da seção 4, formatadas para impressão.
 *
 * **Nenhuma soma acontece aqui, e a ausência é o ponto.** O total impresso é o que o servidor
 * mandou (`valorTotal`, digitado do documento, e `totalDosItens`, somado lá com `BigDecimal` e
 * `HALF_UP`). Somar as linhas no navegador produziria um terceiro número: a prática do contrato
 * arredonda cada linha antes de somar — 182.870,37 + 40.277,78 = 223.148,15 —, enquanto arredondar
 * a soma dá 223.148,14. Um centavo, numa folha que vai para processo administrativo.
 *
 * **A quantidade sai como fração.** "25/30" é o que o termo de recebimento de maio/2026 escreve.
 *
 * @param itens as linhas da ordem, como o servidor as devolveu
 * @returns as linhas prontas para o quadro
 */
export function linhasDaSecao4(itens: ItemDaOs[]): LinhaImpressa[] {
  return itens.map((item) => ({
    item: item.item,
    descricao: item.unidade ? `${item.descricao} (${item.unidade})` : item.descricao,
    metrica: item.metrica === 'PONTO_DE_FUNCAO' ? 'Ponto de Função' : 'VALOR FIXO',
    valorUnitario: reais(item.valorUnitario) ?? '',
    quantidade: item.quantidade,
    valorTotal: reais(item.valorTotalDeclarado) ?? '',
    conferencia: item.divergente ? `Conferência do sistema: ${reais(item.valorTotalDerivado) ?? ''}` : '',
  }))
}

/**
 * O rodapé de valores do quadro.
 *
 * São duas linhas quando os números discordam, e uma quando coincidem: o "Valor total estimado da
 * OS" é o do papel, e a soma das linhas é conferência. Imprimir só a soma corrigiria o documento
 * assinado; imprimir só o rodapé esconderia a divergência.
 *
 * @param ordem a ordem carregada
 * @returns os pares rótulo/valor do rodapé do quadro
 */
export function totaisDaSecao4(ordem: DetalheDaOs): { rotulo: string; valor: string }[] {
  const total = { rotulo: DOCUMENTO_DA_OS.rotulos.totalEstimado, valor: reais(ordem.valorTotal) ?? '' }
  if (ordem.divergenciaDoTotal === 0) {
    return [total]
  }
  return [total, { rotulo: DOCUMENTO_DA_OS.rotulos.somaDasLinhas, valor: reais(ordem.totalDosItens) ?? '' }]
}

/**
 * O código do documento, escrito no rodapé de cada folha.
 *
 * Leva o número do sistema e não o do papel: o número externo pode faltar (nem toda ordem nasceu
 * fora daqui) e não é único entre contratos. Quem tiver a folha na mão acha a ordem por ele.
 *
 * @param ordem a ordem carregada
 * @returns o código, como `OS-0001-2026`
 */
export function codigoDoDocumento(ordem: DetalheDaOs): string {
  return `OS-${ordem.numero.replace('/', '-')}`
}

/**
 * A vigência da ordem, como o modelo a escreve.
 *
 * `dataSimples` nas duas pontas: são datas sem hora, e `new Date('2026-05-06')` volta 05/05/2026 no
 * fuso de Niterói. O início da vigência é o que o período autorizado tem de mais caro.
 *
 * @param ordem a ordem carregada
 * @returns o par de datas já formatado
 */
export function vigenciaDaOs(ordem: DetalheDaOs): { inicio: string; fim: string } {
  return { inicio: dataSimples(ordem.periodoInicio), fim: dataSimples(ordem.periodoFim) }
}
